import { Download, PackageOpen, Search } from 'lucide-react';
import { useState } from 'react';
import { CreatorLink } from '../components/CreatorLink';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { DeviceIcon } from '../components/DeviceIcon';
import { Link } from '../components/Link';
import { PackDownloadButton } from '../components/PackDownloadButton';
import { catalog, profilesForPack } from '../lib/catalog';
import { downloadCountForPack, formatDownloads } from '../lib/downloadCounts';
import { packPath } from '../lib/navigation';
import { deviceSearchText } from '../lib/deviceNames';

interface PacksPageProps {
  notify: (message: string, tone?: 'success' | 'error') => void;
}

type PackSort = 'downloads' | 'count' | 'name' | 'updated';

export function PacksPage({ notify }: PacksPageProps) {
  const [query, setQuery] = useState('');
  const [device, setDevice] = useState('');
  const [sort, setSort] = useState<PackSort>('downloads');
  const normalized = query.toLowerCase().trim();
  const packs = [...catalog.packs]
    .filter((pack) => pack.profileCount > 1)
    .filter((pack) => !device || pack.device === device)
    .filter((pack) => !normalized || `${pack.name} ${deviceSearchText(pack.device)} ${pack.creator} ${pack.connections.join(' ')}`.toLowerCase().includes(normalized))
    .sort((left, right) => {
      if (sort === 'count') return right.profileCount - left.profileCount || left.name.localeCompare(right.name);
      if (sort === 'name') return left.name.localeCompare(right.name);
      if (sort === 'updated') return Date.parse(right.latestUpdatedAt) - Date.parse(left.latestUpdatedAt) || left.name.localeCompare(right.name);
      return downloadCountForPack(right) - downloadCountForPack(left) || left.name.localeCompare(right.name);
    });

  return (
    <>
      <div className="page-heading split-heading">
        <div><h1>Profile packs</h1><p>Download a creator’s complete collection, or open a pack to pick individual profiles.</p></div>
        <div className="heading-stat"><strong>{catalog.packs.filter((pack) => pack.profileCount > 1).length}</strong><span>multi-profile packs</span></div>
      </div>
      <div className="pack-toolbar">
        <label className="input-with-icon"><Search size={16} /><span className="sr-only">Search profile packs</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search packs…" /></label>
        <label><span className="sr-only">Filter by device</span><select value={device} onChange={(event) => setDevice(event.target.value)}><option value="">All devices</option>{catalog.devices.map((value) => <option key={value}>{value}</option>)}</select></label>
        <label><span className="sr-only">Sort packs</span><select value={sort} onChange={(event) => setSort(event.target.value as PackSort)}><option value="downloads">Most downloaded</option><option value="count">Most profiles</option><option value="updated">Recently updated</option><option value="name">Name A–Z</option></select></label>
      </div>
      <p className="results-summary" aria-live="polite">Showing {packs.length} profile pack{packs.length === 1 ? '' : 's'}</p>
      <div className="pack-list-grid">
        {packs.map((pack) => {
          const profiles = profilesForPack(pack);
          return (
            <article className="pack-list-card" key={pack.id}>
              <DeviceIcon device={pack.device} compact />
              <div className="pack-list-content">
                <p className="pack-device">{pack.device}</p>
                <h2><Link href={packPath(pack)}>{pack.name}</Link></h2>
                <p>By <CreatorLink name={pack.creator} /> · {pack.profileCount} profiles</p>
                <div className="tag-row">{pack.connections.map((value) => <ConnectionBadge key={value} connection={value} />)}</div>
              </div>
              <div className="pack-list-actions">
                <span><Download size={14} />{formatDownloads(downloadCountForPack(pack))}</span>
                <PackDownloadButton pack={pack} profiles={profiles} notify={notify} className="button secondary small" />
              </div>
            </article>
          );
        })}
      </div>
      {!packs.length && <div className="empty-state"><PackageOpen size={34} /><h3>No packs found</h3><p>Try a different search or device.</p></div>}
    </>
  );
}
